import React, { useState } from 'react';
import { Device } from '../../redux/types/devices';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Spinner from '@material-ui/core/CircularProgress';
import { navTo } from '../../browserHsitory';
import {
  Typography,
  CardMedia,
  WithStyles,
  ExpansionPanel,
  Box,
  Button,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import styles from './styles';
import { Query } from '../../utils/query';
import useDevices from './hook/useDevices.hook';
import adchubImg from '../../assets/img/adc_hub.png';
import Grid from '@material-ui/core/Grid';
import DeviceStatusBadge from '../../components/DeviceStatusBadge';
import { getDeviceStatusIcon, getLockStatusIcon } from '../../utils/hardwareStatus';

type Props = {
  devices: Device[];
  loadDevices: (query: Query) => void;
  loading: boolean;
  markPropertyAsSold: (propertyId: string | number) => void;
  markingAsSold: boolean;
  builderId: number;
} & WithStyles<typeof styles>;

const Devices: React.FC<Props> = props => {
  const {
    classes,
    devices,
    loadDevices,
    loading,
    markPropertyAsSold,
    markingAsSold,
    builderId,
  } = props;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | number | null>(null);

  useDevices(loadDevices, builderId);

  const handleMarkAsSold = (propertyId: string | number) => {
    setSelectedPropertyId(propertyId);
    setConfirmDialogOpen(true);
  };

  const handleConfirm = () => {
    if (selectedPropertyId) {
      markPropertyAsSold(selectedPropertyId);
    }
    setConfirmDialogOpen(false);
    setSelectedPropertyId(null);
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
    setSelectedPropertyId(null);
  };

  return(
    <>
      <ExpansionPanel
        classes={{ root: classes.expandRoot }}
        expanded={true}
      >
        <ExpansionPanelSummary className={classes.expansionHeader}>
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <Typography className={classes.sectionTitle}>Alarm Devices</Typography>
              <Chip
                label={devices?.length || 0}
                className={classes.deviceCount}
                size="small"
              />
            </Box>
            <Button
              id="fs-export-properties"
              size="small"
              className={classes.exportButton}
              onClick={() => {}}
            >
              Export All
            </Button>
          </Box>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className={classes.expansionContent}>
          <Grid container spacing={3}>
            {devices && devices.map((device, index) => (
              <Grid
                item
                xs={3}
                sm={3}
                md={3}
                lg={3}
                xl={3}
                key={index}
              >
                <Card
                  role="presentation"
                  className={classes.card}
                >
                  <div className={classes.cardHeader}>
                    <CardMedia
                      className={classes.icon}
                      image={adchubImg}
                    />
                    <div className={classes.statusBadge}>
                      {device.property_id ? (
                        <Chip label="Attached" className={classes.assignedBadge} size="small" />
                      ) : (
                        <Chip label="Available" className={classes.availableBadge} size="small" />
                      )}
                    </div>
                  </div>

                  <CardContent className={classes.cardContent}>
                    <Box className={classes.liveStatusSection}>
                      <DeviceStatusBadge
                        label={`${device.deviceStatus?.label || 'Unknown'}`}
                        variant={device.deviceStatus?.variant}
                        icon={getDeviceStatusIcon(device.deviceStatus)}
                      />
                      <DeviceStatusBadge
                        label={`${device.lockStatus?.label || 'Unknown'}`}
                        variant={device.lockStatus?.variant}
                        icon={getLockStatusIcon(device.lockStatus)}
                      />
                    </Box>

                    <Box className={classes.detailsSection}>
                      <Box className={classes.detailRow}>
                        <Typography className={classes.detailLabel}>
                          Customer ID:
                        </Typography>
                        <Typography className={classes.detailValue}>
                          {device.CustomerId}
                        </Typography>
                      </Box>

                      <Box className={classes.detailRow}>
                        <Typography className={classes.detailLabel}>
                          Unit Description:
                        </Typography>
                        <Typography className={classes.highlightedValue}>
                          {device.UnitDescription}
                        </Typography>
                      </Box>

                      {device.property_id && (
                        <>
                          <Box className={classes.detailRow}>
                            <Typography className={classes.detailLabel}>
                              Property ID:
                            </Typography>
                            <Link
                              className={classes.propertyLink}
                              onClick={(e) => e.stopPropagation()}
                              to={`/properties/${device.property_id}`}
                            >
                              {device.property_id}
                            </Link>
                          </Box>

                          <Box className={classes.detailRow}>
                            <Typography className={classes.detailLabel}>
                              Lot Number:
                            </Typography>
                            <Typography className={classes.detailValue}>
                              {device.lot_number}
                            </Typography>
                          </Box>

                          <Box className={classes.detailRow}>
                            <Typography className={classes.detailLabel}>
                              Community:
                            </Typography>
                            <Typography className={classes.detailValue}>
                              {device.community_name}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>

                    <Box className={classes.actionSection}>
                      {device.property_id ? (
                        <Button
                          variant="outlined"
                          className={classes.maskButton}
                          onClick={() => typeof device.property_id === 'string' || typeof device.property_id === 'number' ? handleMarkAsSold(device.property_id) : null}
                          fullWidth
                          size="small"
                          disabled={markingAsSold}
                        >
                          Mark as sold
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.activateButton}
                          onClick={() => navTo(`devices/${device.CustomerId}?name=${device.UnitDescription}`)}
                          fullWidth
                          size="small"
                        >
                          ACTIVATE
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </ExpansionPanelDetails>
      </ExpansionPanel>
      {loading && (
        <div className={classes.spinnerWrapper}>
          <Spinner />
        </div>
      )}

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancel}
        aria-labelledby="mark-as-sold-dialog-title"
      >
        <DialogTitle id="mark-as-sold-dialog-title">
          Confirm Mark as Sold
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark this property as sold?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            disabled={markingAsSold}
          >
            {markingAsSold ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Devices
